"use client";

import { useEffect, useState } from "react";
import type { TocItem } from "@/lib/insights/toc";
import { useTranslation } from "@/lib/i18n";
import { makeTr } from "@/lib/tools-i18n";
import { INSIGHTS_BG } from "@/lib/tools-i18n/insights";

type Props = {
  items: TocItem[];
};

export function InsightsToc({ items }: Props) {
  const { language } = useTranslation();
  const tr = makeTr(language, INSIGHTS_BG);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || items.length === 0) return;

    const headingEls: HTMLElement[] = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el !== null);

    if (headingEls.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost heading currently intersecting the activation band
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
          return;
        }
        // Fallback: find last heading above viewport
        const above = headingEls
          .filter((el) => el.getBoundingClientRect().top < 120)
          .sort(
            (a, b) =>
              a.getBoundingClientRect().top - b.getBoundingClientRect().top,
          );
        if (above.length > 0) {
          setActiveId(above[above.length - 1].id);
        }
      },
      {
        // Activation band: top 120px–55% of viewport
        rootMargin: "-120px 0px -45% 0px",
        threshold: [0, 1],
      },
    );

    headingEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label={tr("Table of contents")}
      className="hidden xl:block sticky top-24 self-start"
    >
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-3 font-medium">
        {tr("On this page")}
      </div>
      <ul lang="en" className="space-y-1.5 text-sm border-l border-slate-200 dark:border-slate-800">
        {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id} className={item.level === 3 ? "ml-3" : ""}>
              <a
                href={`#${item.id}`}
                className={`block border-l-2 -ml-px pl-3 py-1 transition-colors leading-snug ${
                  isActive
                    ? "border-blue-600 text-slate-900 dark:text-white font-medium"
                    : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                {item.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
